import { EventService } from 'src/app/service/event.service';
import { Events } from 'src/app/service/websocket.service';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { tap, finalize } from 'rxjs/operators';
import { LogsSettings } from 'src/app/model/Setting';
import { GeneralService } from 'src/app/service/general.service';
import { Utils } from 'src/app/utils';
import { Modal } from '../controls/modal/modal.service';
import { ModalMessageComponent } from '../controls/modal-message/modal-message.component';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { UserInfo } from 'src/app/model/User';
import { ModalConfig } from '../controls/modal/modal-config';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-settings-log',
    templateUrl: './settings-log.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-log.component.css'],
    standalone: false
})
export class SettingsLogComponent implements OnInit {

  isLoading:boolean = true;
  isSaving:boolean = false;
  private requestLoggingValidators = [
    Validators.required,
    Validators.min(1),
    Validators.max(30),
    Validators.pattern("^[0-9]*$")
  ];
  settingsForm = new FormGroup({
    logLevelCtrl: new FormControl(),
    httpLogLevelCtrl: new FormControl(),
    SFTPLogLevelCtrl: new FormControl(),
    requestLoggingCtrl: new FormControl(),
    requestLoggingRetentionPeriodCtrl: new FormControl<number>(7),
  });
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;
  settings:LogsSettings;

  constructor(private genService: GeneralService,
              private eventService: EventService,
              private modal: Modal,
              private authService: AuthenticationService,) { }

  get logLevelCtrl() { return this.settingsForm.get('logLevelCtrl'); }
  get httpLogLevelCtrl() { return this.settingsForm.get('httpLogLevelCtrl'); }
  get SFTPLogLevelCtrl() { return this.settingsForm.get('SFTPLogLevelCtrl'); }
  get requestLoggingCtrl() { return this.settingsForm.get('requestLoggingCtrl'); }
  get requestLoggingRetentionPeriodCtrl() { return this.settingsForm.get('requestLoggingRetentionPeriodCtrl')}

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.initData();
  }

  initData(showLoading:boolean = true): void{
    if(showLoading){
      this.isLoading = true;
    }
    this.genService.getLogsSettings().pipe(tap(resp => {
      this.settings = resp.body;
      this.requestLoggingCtrl.valueChanges.subscribe(value => {
        this.setRequestLogValidator(value);
      });
      this.logLevelCtrl.setValue(this.settings.logLevel);
      this.httpLogLevelCtrl.setValue(this.settings.httpLogLevel);
      this.SFTPLogLevelCtrl.setValue(this.settings.SFTPLogLevel);
      this.requestLoggingCtrl.setValue(this.settings.requestLog);
      this.requestLoggingRetentionPeriodCtrl.setValue(this.settings.requestLogRetainDays);
      this.settingsForm.reset(this.settingsForm.value);
    }),
    finalize(() => {
      this.isLoading = false;
    }),
    ).subscribe();
  }

  save(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-logs-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;
          this.settings.logLevel = this.logLevelCtrl.value;
          this.settings.httpLogLevel = this.httpLogLevelCtrl.value;
          this.settings.SFTPLogLevel = this.SFTPLogLevelCtrl.value;
          this.settings.requestLog = this.requestLoggingCtrl.value;

          if (this.settings.requestLog) {
            this.settings.requestLogRetainDays = this.requestLoggingRetentionPeriodCtrl.value;
          }

          this.genService.updateLogsSettings(this.settings).pipe(tap(resp => {
            if (resp.body) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
              this.initData(false);
            } else {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
              this.settingsForm.reset(this.settingsForm.value);
            }
          }),
            finalize(() => {
              this.isSaving = false;
              this.savingBtn = $localize`Save`;
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-logs-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
        }
        sub.unsubscribe();
      });
  }

  setRequestLogValidator(setValidator:boolean): void {
    if(setValidator){
      this.requestLoggingRetentionPeriodCtrl.setValidators(this.requestLoggingValidators);
    }else{
      this.requestLoggingRetentionPeriodCtrl.clearValidators();
    }

    this.requestLoggingRetentionPeriodCtrl.updateValueAndValidity();
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-logs-unsaved-changes');
  }
}
